/// Bearer-token storage for Capacitor sign-in. The web build uses
/// the goa_session cookie (Domain=.goastern.com) like before; the
/// Capacitor build can't get that cookie because the WebView origin
/// is `https://localhost`, so we keep the JWT in localStorage and
/// inject `Authorization: Bearer <token>` into every API call.
///
/// The token is delivered by goastern-auth as a fragment on the
/// `goastern-radar://auth/complete` deep link (caught by Android's
/// intent filter, fired into the SPA via `@capacitor/app`'s
/// `appUrlOpen` event — see `src/main.tsx`).

const KEY = "radar-sim.access-token";

export function storeToken(token: string): void {
  try {
    window.localStorage.setItem(KEY, token);
  } catch {
    /* private-mode safari / SSR / disabled storage — no-op */
  }
}

export function loadToken(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}

/// Pull the access token out of a deep-link URL of the form
/// `goastern-radar://auth/complete#access=<jwt>&refresh=…`. Returns
/// null when the URL doesn't carry one.
export function extractTokenFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hash = parsed.hash.startsWith("#")
      ? parsed.hash.slice(1)
      : parsed.hash;
    if (hash.length === 0) return null;
    const params = new URLSearchParams(hash);
    return params.get("access");
  } catch {
    return null;
  }
}
